import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import StatCard from '../../components/admin/StatCard';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Users, TrendingUp, Percent, Clock, Activity, Target
} from 'lucide-react';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const PRIMARY = '#0071e3';
const GOLD = '#d4af37';
const NAVY = '#1e3a8a';
const GREEN = '#10b981';
const RED = '#ef4444';
const PURPLE = '#8b5cf6';

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: 'bottom', labels: { font: { family: 'Inter, sans-serif', size: 12 }, padding: 16 } },
    tooltip: {
      backgroundColor: 'rgba(255,255,255,0.95)', titleColor: NAVY, bodyColor: '#475569',
      borderColor: '#e2e8f0', borderWidth: 1, padding: 12, cornerRadius: 8,
    },
  },
  scales: {
    x: { grid: { color: '#f1f5f9', drawBorder: false }, ticks: { font: { size: 11 }, color: '#64748b' } },
    y: { grid: { color: '#f1f5f9', drawBorder: false }, ticks: { font: { size: 11 }, color: '#64748b' } },
  },
};

const noScaleOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: 'bottom', labels: { font: { family: 'Inter, sans-serif', size: 12 }, padding: 16 } },
    tooltip: {
      backgroundColor: 'rgba(255,255,255,0.95)', titleColor: NAVY, bodyColor: '#475569',
      borderColor: '#e2e8f0', borderWidth: 1, padding: 12, cornerRadius: 8,
    },
  },
};

export default function AdminAnalytics() {
  const [tab, setTab] = useState('users');
  const [userStats, setUserStats] = useState(null);
  const [requestStats, setRequestStats] = useState(null);
  const [helperStats, setHelperStats] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [u, r, h, p] = await Promise.all([
          adminAPI.getUserStats(),
          adminAPI.getRequestStats(),
          adminAPI.getHelperStats(),
          adminAPI.getPlatformStats(),
        ]);
        setUserStats(u);
        setRequestStats(r);
        setHelperStats(h);
        setPlatformStats(p);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const tabs = [
    { key: 'users', label: 'Users' },
    { key: 'requests', label: 'Requests' },
    { key: 'helpers', label: 'Helpers' },
    { key: 'platform', label: 'Platform Health' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-navy-900">Analytics</h1>
        <p className="text-navy-500 mt-1">In-depth platform metrics and trends.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-navy-100 p-1 rounded-apple w-fit">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-apple transition-all duration-200 ${
              tab === t.key ? 'bg-white text-navy-900 shadow-apple-sm' : 'text-navy-500 hover:text-navy-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'users' && <UsersTab data={userStats} />}
      {tab === 'requests' && <RequestsTab data={requestStats} />}
      {tab === 'helpers' && <HelperTab data={helperStats} />}
      {tab === 'platform' && <PlatformTab data={platformStats} />}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-navy-700 mb-4">{title}</h3>
      <div style={{ height: 280 }}>{children}</div>
    </div>
  );
}

function UsersTab({ data }) {
  if (!data) return null;

  const growthData = {
    labels: (data.userGrowth || []).map(d => {
      const [y, m] = d.month.split('-');
      return new Date(y, m - 1).toLocaleDateString('en', { month: 'short', year: '2-digit' });
    }),
    datasets: [{
      label: 'New Users',
      data: (data.userGrowth || []).map(d => d.count),
      borderColor: PRIMARY, backgroundColor: 'rgba(0,113,227,0.1)',
      fill: true, tension: 0.4, pointRadius: 4, borderWidth: 2,
    }],
  };

  const typeData = {
    labels: (data.userTypes || []).map(d => d.type?.replace('_', ' ')),
    datasets: [{
      data: (data.userTypes || []).map(d => d.count),
      backgroundColor: [PRIMARY, PURPLE, RED],
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  const statusData = {
    labels: (data.userStatus || []).map(d => d.status),
    datasets: [{
      data: (data.userStatus || []).map(d => d.count),
      backgroundColor: [GREEN, '#fbbf24', RED, '#94a3b8'],
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="User Growth (Last 12 Months)">
          <Line data={growthData} options={chartOptions} />
        </ChartCard>
        <ChartCard title="User Types">
          <Doughnut data={typeData} options={{ ...noScaleOptions, cutout: '60%' }} />
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="User Status">
          <Doughnut data={statusData} options={{ ...noScaleOptions, cutout: '60%' }} />
        </ChartCard>
        <ChartCard title="Registration Trends (30 Days)">
          <Bar
            data={{
              labels: (data.registrationTrends || []).map(d => {
                const parts = d.date.split('-');
                return `${parts[2]}/${parts[1]}`;
              }),
              datasets: [{
                label: 'Registrations', data: (data.registrationTrends || []).map(d => d.count),
                backgroundColor: PRIMARY, borderRadius: 4,
              }],
            }}
            options={chartOptions}
          />
        </ChartCard>
        <ChartCard title="Top Countries">
          <Bar
            data={{
              labels: (data.topCountries || []).map(d => d.country),
              datasets: [{
                label: 'Helpers', data: (data.topCountries || []).map(d => d.helper_count),
                backgroundColor: GOLD, borderRadius: 4,
              }],
            }}
            options={{ ...chartOptions, indexAxis: 'y' }}
          />
        </ChartCard>
      </div>
    </div>
  );
}

function RequestsTab({ data }) {
  if (!data) return null;

  const volumeData = {
    labels: (data.volumeOverTime || []).map(d => {
      const [y, m] = d.month.split('-');
      return new Date(y, m - 1).toLocaleDateString('en', { month: 'short' });
    }),
    datasets: [
      {
        label: 'Total', data: (data.volumeOverTime || []).map(d => d.total),
        backgroundColor: PRIMARY, borderRadius: 4,
      },
      {
        label: 'Completed', data: (data.volumeOverTime || []).map(d => d.completed),
        backgroundColor: GREEN, borderRadius: 4,
      },
    ],
  };

  const statusColors = { pending: '#fbbf24', active: PRIMARY, matched: PURPLE, confirmed: '#6366f1', in_progress: '#06b6d4', completed: GREEN, cancelled: RED };
  const statusData = {
    labels: (data.statusDistribution || []).map(d => d.status?.replace('_', ' ')),
    datasets: [{
      data: (data.statusDistribution || []).map(d => d.count),
      backgroundColor: (data.statusDistribution || []).map(d => statusColors[d.status] || '#94a3b8'),
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Avg Apps / Request" value={data.avgApplicationsPerRequest || 0} color="primary" />
        <StatCard icon={Percent} label="Urgent %" value={`${data.urgentPercentage || 0}%`} color="red" />
        <StatCard icon={Target} label="Completion Rate" value={`${(data.completionRates || []).slice(-1)[0]?.rate || 0}%`} color="green" />
        <StatCard icon={Activity} label="Top Port" value={(data.topPorts || [])[0]?.port_name || 'N/A'} color="navy" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Request Volume (Last 12 Months)">
          <Bar data={volumeData} options={chartOptions} />
        </ChartCard>
        <ChartCard title="Request Status Distribution">
          <Doughnut data={statusData} options={{ ...noScaleOptions, cutout: '60%' }} />
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Top Ports">
          <Bar
            data={{
              labels: (data.topPorts || []).map(d => d.port_name),
              datasets: [{
                label: 'Requests', data: (data.topPorts || []).map(d => d.request_count),
                backgroundColor: PRIMARY, borderRadius: 4,
              }],
            }}
            options={{ ...chartOptions, indexAxis: 'y' }}
          />
        </ChartCard>
        <ChartCard title="Peak Hours">
          <Line
            data={{
              labels: (data.peakHours || []).map(d => `${String(d.hour).padStart(2, '0')}:00`),
              datasets: [{
                label: 'Requests', data: (data.peakHours || []).map(d => d.count),
                borderColor: GOLD, backgroundColor: 'rgba(212,175,55,0.1)',
                fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2,
              }],
            }}
            options={chartOptions}
          />
        </ChartCard>
      </div>
    </div>
  );
}

function HelperTab({ data }) {
  if (!data) return null;

  const verifyData = {
    labels: (data.verificationPipeline || []).map(d => d.status),
    datasets: [{
      data: (data.verificationPipeline || []).map(d => d.count),
      backgroundColor: [GREEN, '#fbbf24', RED],
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  const ratingData = {
    labels: (data.ratingDistribution || []).map(d => `${d.stars} star${d.stars !== '1' ? 's' : ''}`),
    datasets: [{
      label: 'Ratings', data: (data.ratingDistribution || []).map(d => d.count),
      backgroundColor: GOLD, borderRadius: 4,
    }],
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Availability Rate" value={`${data.availabilityRate || 0}%`} color="green" />
        <StatCard icon={Clock} label="Avg Experience" value={`${data.averageExperience || 0} yrs`} color="primary" />
        <StatCard icon={Target} label="Skills Tracked" value={(data.skillsBreakdown || []).length} color="purple" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Verification Pipeline">
          <Doughnut data={verifyData} options={{ ...noScaleOptions, cutout: '60%' }} />
        </ChartCard>
        <ChartCard title="Rating Distribution">
          <Bar data={ratingData} options={chartOptions} />
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Helper Growth">
          <Line
            data={{
              labels: (data.helperGrowth || []).map(d => {
                const [y, m] = d.month.split('-');
                return new Date(y, m - 1).toLocaleDateString('en', { month: 'short' });
              }),
              datasets: [{
                label: 'New Helpers', data: (data.helperGrowth || []).map(d => d.count),
                borderColor: PURPLE, backgroundColor: 'rgba(139,92,246,0.1)',
                fill: true, tension: 0.4, pointRadius: 4, borderWidth: 2,
              }],
            }}
            options={chartOptions}
          />
        </ChartCard>
        <ChartCard title="Skills Breakdown">
          <Bar
            data={{
              labels: (data.skillsBreakdown || []).slice(0, 10).map(d => d.skill),
              datasets: [{
                label: 'Helpers', data: (data.skillsBreakdown || []).slice(0, 10).map(d => d.count),
                backgroundColor: PRIMARY, borderRadius: 4,
              }],
            }}
            options={{ ...chartOptions, indexAxis: 'y' }}
          />
        </ChartCard>
      </div>

      {/* Top Helpers Table */}
      {data.topHelpers && data.topHelpers.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-navy-700 mb-4">Top Rated Helpers</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-navy-200">
              <thead className="bg-navy-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-navy-500 uppercase">Helper</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-navy-500 uppercase">Rating</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-navy-500 uppercase">Jobs</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-navy-500 uppercase">Reviews</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {data.topHelpers.map(h => (
                  <tr key={h.id}>
                    <td className="px-4 py-2.5 text-sm font-medium text-navy-900">{h.first_name} {h.last_name}</td>
                    <td className="px-4 py-2.5 text-sm text-gold-600 font-semibold">{h.average_rating?.toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-sm text-navy-700">{h.total_jobs}</td>
                    <td className="px-4 py-2.5 text-sm text-navy-700">{h.rating_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformTab({ data }) {
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Target} label="Match Rate (30d)" value={`${data.matchRate || 0}%`} color="primary" />
        <StatCard icon={Percent} label="Completion Rate (30d)" value={`${data.completionRate || 0}%`} color="green" />
        <StatCard icon={TrendingUp} label="Avg Apps / Request" value={data.avgApplicationsPerRequest || 0} color="purple" />
        <StatCard icon={Activity} label="Cancel Rate (30d)" value={`${data.cancellationRate || 0}%`} color="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Trend */}
        <div className="card">
          <h3 className="text-sm font-semibold text-navy-700 mb-4">Weekly Trends</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-navy-50 rounded-apple p-4">
              <p className="text-xs text-navy-500 mb-1">New Users This Week</p>
              <p className="text-xl font-bold text-navy-900">{data.userGrowth?.thisWeek || 0}</p>
              <p className={`text-xs mt-1 font-medium ${(data.userGrowth?.change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(data.userGrowth?.change || 0) >= 0 ? '+' : ''}{data.userGrowth?.change || 0} vs last week
              </p>
            </div>
            <div className="bg-navy-50 rounded-apple p-4">
              <p className="text-xs text-navy-500 mb-1">New Requests This Week</p>
              <p className="text-xl font-bold text-navy-900">{data.requestGrowth?.thisWeek || 0}</p>
              <p className={`text-xs mt-1 font-medium ${(data.requestGrowth?.change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(data.requestGrowth?.change || 0) >= 0 ? '+' : ''}{data.requestGrowth?.change || 0} vs last week
              </p>
            </div>
          </div>
        </div>

        {/* Message Activity */}
        <ChartCard title="Message Activity (Last 7 Days)">
          <Bar
            data={{
              labels: (data.messageActivity || []).map(d => {
                const parts = d.date.split('-');
                return `${parts[2]}/${parts[1]}`;
              }),
              datasets: [{
                label: 'Messages', data: (data.messageActivity || []).map(d => d.count),
                backgroundColor: PRIMARY, borderRadius: 4,
              }],
            }}
            options={chartOptions}
          />
        </ChartCard>
      </div>

      {/* No-show rate */}
      <div className="card">
        <h3 className="text-sm font-semibold text-navy-700 mb-4">Service Reliability</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-apple p-4 text-center">
            <p className="text-xs text-navy-500 mb-1">No-Show Rate</p>
            <p className="text-2xl font-bold text-green-700">{data.noShowRate || 0}%</p>
          </div>
          <div className="bg-primary-50 rounded-apple p-4 text-center">
            <p className="text-xs text-navy-500 mb-1">Match Rate</p>
            <p className="text-2xl font-bold text-primary-700">{data.matchRate || 0}%</p>
          </div>
          <div className="bg-yellow-50 rounded-apple p-4 text-center">
            <p className="text-xs text-navy-500 mb-1">Cancellation Rate</p>
            <p className="text-2xl font-bold text-yellow-700">{data.cancellationRate || 0}%</p>
          </div>
        </div>
      </div>
    </div>
  );
}
